import { mobileAccess, virtualDir } from "#skyward_global";
import { registerEvents } from "./skyward.ui.events";
import { checkLoadContent, updatePageState as updateWindowPageState } from "#skyward_window";
import { CallbackStore, FacadesForCollection } from "./types";
import { getSvg, swapSvg } from "./skyward.ui.svg";
import { Key } from "./types/skyward/svg";
import { createCollectionHandler, CollectionHandler } from "./skyward.ui.keyboardNavigation";

const skyward = window.skyward;
const tabFunctions: { [key: string]: (element: HTMLElement) => void } = {};

const classes =
{
    tabListItems: "js-tabListItems",
    tabListItemGroupName: "tabListItemGroupName",
    tabListItemGroup: "tabListItemGroup",
    tabListItem: "tabListItem",
    current: "current",
    leftMenuSidebarHeader: "js-leftMenuSidebarHeader",
    leftMenuSidebarSliderButton: "js-leftMenuSidebarSliderButton",
    expandAllTabListItemGroup: "js-expandAllTabListItemGroup",
    collapseAllTabListItemGroup: "js-collapseAllTabListItemGroup",
    menuContentArea: "menuContentArea",
    contentArea: "contentArea",
    mobileToolbarControlledContent: "js-mobileToolbarControlledContent",
    tablistCollapsed: "tablist--collapsed"
};

const dataKeys =
{
    id: "id",
    profileModule: "profileModule",
    profileId: "profileId",
    clickTarget: "clickTarget",
    isForNavigation: "isForNavigation"
};

type PageStateData =
    {
        expandedModules?: string[],
        profileScroll?: number,
        tab?: string,
        UrlReferrer?: string
    };

type TabListEvents =
    {
        toggleLeftMenuSidebar: TabListCallbackStore;
    };

type TabListCallback = () => void;

type TabListCallbackStore = CallbackStore<TabListCallback>;

const localEventCallbacks: TabListEvents =
{
    toggleLeftMenuSidebar: skyward.events.getCallbackStore()
};

export const eventCallbacks: FacadesForCollection<TabListEvents> = skyward.events.getCallbackStoreFacadeCollection(localEventCallbacks);

export function registerTabFunction(name: string, callback: (element: HTMLElement) => void)
{
    tabFunctions[name] = callback;
}

// Temporarily assign exports to skyward.tablist
// TODO: delete once all dependents have been converted to Typescript
$.extend((skyward as any).tablist ??= {}, {
    eventCallbacks,
    registerTabFunction,
});

const events =
{
    init
};

registerEvents("tablist", events);

function init(this: JQuery<HTMLElement>, tabList: JQuery<HTMLElement>)
{
    const tabListScrolling = tabList.find(`.${classes.tabListItems}`);
    const tabListGroupButtons = tabList.find(`.${classes.tabListItemGroupName}`);
    const tabListCollection = createCollectionHandler(tabListGroupButtons);
    const isForNavigation = tabList.data(dataKeys.isForNavigation);

    if (mobileAccess)
    {
        const tabListGroups = tabList.find(`.${classes.tabListItemGroup}`);
        initMobileTabList(tabList, tabList, tabListGroups);
    }

    const scrollValue = parseFloat(tabList[0].getAttribute("data-scroll-top") || "");

    // if we already have a set scroll top, use that
    if (scrollValue && !isNaN(scrollValue))
    {
        tabListScrolling.scrollTop(scrollValue);
    }
    // otherwise get our current group as close to the top as we can
    else
    {
        const currentTabGroup = tabList.find(`.${classes.tabListItem}.${classes.current}`).closest(`.${classes.tabListItemGroup}`)[0];
        if (currentTabGroup)
        {
            // Manually scroll instead of using native Element.ScrollIntoView() as it messes with the tab order, and jQuery.scrollintoview doesn't 
            // put the element as close to the top as possible.
            //
            // Scroll the amount we need for our current group to be at the top of the tab list. We don't need to 
            // make sure we don't go over the max (tabListScrolling[0].scrollHeight - tabListScrolling[0].clientHeight) as 
            // the browser will automatically cap it at that for us
            tabListScrolling[0].scrollTop = currentTabGroup.getBoundingClientRect().top - tabListScrolling[0].getBoundingClientRect().top;
        }
    }
    tabListScrolling.css("visibility", "visible");

    updatePageStateScrollPosition();

    tabListScrolling.on("click", `.${classes.tabListItemGroupName}`, function (this: HTMLElement)
    {
        tabListScrolling.off("scroll");
        const tabBtn = $(this);
        if (tabBtn.attr("aria-expanded") === "true")
        {
            collapseModule(tabBtn).done(function ()
            {
                updatePageState();
                updateUserProfileTabStatus();
                updatePageStateScrollPosition();
            });
        }
        else
        {
            expandModule(tabBtn).done(function ()
            {
                scrollProfileModuleIntoView(tabBtn.closest(`.${classes.tabListItemGroup}`), tabListScrolling).done(function ()
                {
                    updatePageState();
                    updateUserProfileTabStatus();
                    updatePageStateScrollPosition();
                });
            });
        }
    });

    tabListScrolling.on("click", `.${classes.tabListItem}`, function (this: HTMLElement)
    {
        const tabListItem = $(this);
        const tabListItemData = tabListItem.data();

        if (tabListItemData.function.length > 0)
        {
            return tabFunctions[tabListItemData.function](this);
        }

        updatePageState().then(function (data: PageStateData)
        {
            if (typeof data === "object" && data !== null)
            {
                data.tab = tabListItemData.tab;
                data.UrlReferrer = tabListItemData.urlreferrer;
            }

            const settings =
            {
                activityIndicator: { hideGlass: true }
            };

            return checkLoadContent(tabListItemData.target, tabListItemData.area, tabListItemData.controller, tabListItemData.action, tabListItemData.primarykey, data, null, undefined, null, undefined, tabListItemData.profilescreenid, null, null, null, settings);
        });
    });

    const itemCollection: CollectionHandler<HTMLElement> =
    {
        getPreviousItem: function (elem) { return elem?.previousElementSibling as HTMLElement | undefined || elem?.parentElement?.lastElementChild as HTMLElement },
        getNextItem: function (elem) { return elem?.nextElementSibling as HTMLElement | undefined || elem?.parentElement?.firstElementChild as HTMLElement; }
    };

    tabListScrolling.on("keydown", `.${classes.tabListItemGroupName}`, function (this: HTMLElement, event)
    {
        return handleArrowKeys.call(this, event, tabListCollection);
    });
    tabListScrolling.on("keydown", `.${classes.tabListItem}`, function (this: HTMLElement, event)
    {
        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
        if (eventKeys.ENTER || (eventKeys.SPACE && !isForNavigation))
        {
            $(this).trigger("click");
            event.preventDefault();
            return false;
        }
        else
        {
            return handleArrowKeys.call(this, event, itemCollection);
        }
    });

    initHeader();

    function handleArrowKeys(this: HTMLElement, event: JQuery.KeyDownEvent, collection: CollectionHandler<HTMLElement>)
    {
        let nextElement;
        const eventKeys = skyward.keys.getKeyNamesFromEvent(event);
        switch (true)
        {
            case eventKeys.ARROW_UP:
                nextElement = collection.getPreviousItem(this);
                break;
            case eventKeys.ARROW_DOWN:
                nextElement = collection.getNextItem(this);
                break;
        }
        if (nextElement)
        {
            nextElement.focus();
            event.preventDefault();
            return false;
        }

        return;
    }

    function scrollProfileModuleIntoView(profileModule: JQuery<HTMLElement>, scrollContainer: JQuery<HTMLElement>)
    {
        const deferredAnimation = $.Deferred();

        // scrollintoview() does not call complete() if the element cannot be scrolled
        if (scrollContainer.is(":scrollable"))
        {
            profileModule.scrollintoview(
                {
                    duration: 500,
                    direction: "vertical",
                    complete: function ()
                    {
                        deferredAnimation.resolve();
                    }
                });
        }
        else
        {
            deferredAnimation.resolve();
        }

        return deferredAnimation.promise();
    }

    function collapseModule(profileButton: JQuery<HTMLElement>)
    {
        const deferred = $.Deferred();
        const expanded = profileButton.attr("aria-expanded") === "true";
        if (!expanded)
        {
            return deferred.resolve().promise();
        }
        // getElementById is safer than $() in case there are special characters in the ID
        const tabChildren = $(document.getElementById(profileButton.attr("aria-controls")!)!);
        tabChildren.animateClose(
            {
                complete: function ()
                {
                    profileButton.attr("aria-expanded", "false");
                    swapSvg(profileButton.find("svg"), "Expand", "LeftMenuSidebarLI").attr("aria-hidden", "true");
                    deferred.resolve();
                }
            });
        return deferred;
    }

    function expandModule(profileButton: JQuery<HTMLElement>)
    {
        const deferred = $.Deferred();
        const expanded = profileButton.attr("aria-expanded") === "true";
        if (expanded)
        {
            return deferred.resolve().promise();
        }
        const tabChildren = $(document.getElementById(profileButton.attr("aria-controls")!)!);
        tabChildren.animateOpen(
            {
                complete: function ()
                {
                    profileButton.attr("aria-expanded", "true");
                    swapSvg(profileButton.find("svg"), "Collapse", "LeftMenuSidebarLI").attr("aria-hidden", "true");
                    deferred.resolve();
                }
            });
        return deferred;
    }

    function expandAllModules(this: HTMLElement)
    {
        const button = $(this);
        button.off("click", expandAllModules);
        const deferred: JQuery.Promise<any>[] = [];
        tabListGroupButtons.each(function ()
        {
            deferred.push(expandModule($(this)));
        });
        $.whenAll(deferred).done(function ()
        {
            updatePageState();
            updateUserProfileTabStatus();
            tabList.trigger("sidebarStateChanged");
            button.on("click", expandAllModules);
        });
    }

    function collapseAllModules(this: HTMLElement)
    {
        const button = $(this);
        button.off("click", collapseAllModules);
        const deferred: JQuery.Promise<any>[] = [];
        tabListGroupButtons.each(function ()
        {
            deferred.push(collapseModule($(this)));
        });
        $.whenAll(deferred).done(function ()
        {
            updatePageState();
            updateUserProfileTabStatus();
            tabList.trigger("sidebarStateChanged");
            button.on("click", collapseAllModules);
        });
    }

    function updatePageStateScrollPosition()
    {
        tabListScrolling.debounce("scroll", function ()
        {
            updatePageState();
        }, 250);
    }

    function updateUserProfileTabStatus()
    {
        const openTabs = tabList.find(`.${classes.tabListItemGroupName}[aria-expanded="true"]`)
            .closest(`.${classes.tabListItemGroup}`)
            .toArray()
            .map(function (value: HTMLElement) { return $(value).data(dataKeys.profileModule); });
        const profileID = tabList.data(dataKeys.profileId);

        if (!profileID)
        {
            return $.Deferred().resolve().promise();
        }

        return $.ajax(
            {
                url: `${virtualDir}Security/UserProfileTabStatus/Update`,
                type: "POST",
                dataType: "JSON",
                activityIndicator: { silent: true },
                data: { "profileID": profileID, "openTabs": JSON.stringify(openTabs) }
            }).promise();
    }

    function updatePageState()
    {
        const updates = [];
        const params: { key: string, value: string[] | number | null }[] = [];

        const currentTabData = tabList.find(`.${classes.tabListItem}.${classes.current}`).data();
        if (currentTabData == null)
        {
            return $.Deferred().resolve(undefined);
        }

        const data = getTabListData();

        params.push(
            {
                key: "expandedModules",
                value: data.expandedModules == undefined ? null : data.expandedModules
            });

        if (data.profileScroll != null && data.profileScroll > -1)
        {
            params.push(
                {
                    key: "profileScroll",
                    value: data.profileScroll
                });
        }

        const tabListData = tabList.data();
        if (tabListData != null && tabListData.profile != null)
        {
            params.push(
                {
                    key: "profile",
                    value: tabListData.profile
                });
        }

        const tabListParams =
        {
            id: "tabList",
            items: params
        };

        updates.push(tabListParams);
        return updateWindowPageState(updates)
            .then(function ()
            {
                return data;
            }) as JQuery.Promise<any>;

        function getTabListData()
        {
            let data: PageStateData = {};

            if (currentTabData.target === "detailsPanel")
            {
                const postedData = skyward.detailsPanel.getData();
                data = $.extend(postedData, data);
            }
            data.profileScroll = tabListScrolling.scrollTop();

            if (!tabList.data(dataKeys.profileId))
            {
                const expandedModuleTabs = tabList.find(`.${classes.tabListItemGroupName}[aria-expanded="true"]`).closest(`.${classes.tabListItemGroup}`);
                const expandedModules: string[] = [];
                for (let i = 0; i < expandedModuleTabs.length; i++)
                {
                    if (currentTabData.profilescreenid)
                    {
                        const profileModule = expandedModuleTabs.eq(i).data(dataKeys.profileModule) as string;
                        expandedModules.push(profileModule);
                    }
                    else
                    {
                        const groupId = expandedModuleTabs.eq(i).data(dataKeys.id) as string;
                        expandedModules.push(groupId);
                    }
                }

                if (expandedModules.length > 0)
                {
                    data.expandedModules = expandedModules;
                }
            }

            return data;
        }
    }

    function initMobileTabList(context: JQuery<HTMLElement>, tabList: JQuery<HTMLElement>, tabListGroups: JQuery<HTMLElement>)
    {
        const mobileTabList = $("<div />", { class: "mobileTabList" });
        const mobileTabList_content = $("<div />", { class: "mobileTabList__content" });
        const mobileTabList_select = $("<select />", { class: "mobileTabList__select" }) as JQuery<HTMLSelectElement>;

        if (tabListGroups.length)
        {
            const mobileTabList_label = $("<span />", { class: "mobileTabList__label" });

            tabListGroups.each(function ()
            {
                const ulGroup = $(this);
                const ulButton = ulGroup.find(`.${classes.tabListItemGroupName}`);
                const optionGroup = $("<optgroup />",
                    {
                        label: ulButton.text()
                    }) as JQuery<HTMLOptGroupElement>;

                const controlledId = ulButton.attr("aria-controls")!;
                $(document.getElementById(controlledId)!).find(`li.${classes.tabListItem}`).each(function ()
                {
                    const liItem = $(this) as JQuery<HTMLLIElement>;
                    if (liItem.hasClass("current"))
                    {
                        mobileTabList_label.text(optionGroup.attr("label")!);
                    }
                    makeOptionFromItem(liItem, optionGroup);
                });

                mobileTabList_select.append(optionGroup);
            });

            mobileTabList_content.append(mobileTabList_label);
        }
        else
        {
            tabListScrolling.find(`li.${classes.tabListItem}`).each(function ()
            {
                makeOptionFromItem($(this), mobileTabList_select);
            });
        }

        if (mobileTabList_select.children().length)
        {
            mobileTabList_content.append(mobileTabList_select);
            mobileTabList.append(mobileTabList_content);

            const contentArea = context.find(`.${classes.contentArea}`);
            const toolbarControlledContent = contentArea.find(`.${classes.mobileToolbarControlledContent}`);

            if (toolbarControlledContent.length)
            {
                toolbarControlledContent.prepend(mobileTabList);
            }
            else if (contentArea.length)
            {
                contentArea.prepend(mobileTabList);
            }
            else
            {
                mobileTabList.insertBefore(tabList);
            }

            mobileTabList_select.on("change", function (this: HTMLSelectElement)
            {
                const clickTarget = $(this.selectedOptions).first().data(dataKeys.clickTarget);
                if (clickTarget)
                {
                    clickTarget.click();
                }
            });
        }

        function makeOptionFromItem(liItem: JQuery<HTMLLIElement>, itemParent: JQuery<HTMLSelectElement> | JQuery<HTMLOptGroupElement>)
        {
            const option = $("<option />").html(liItem.text());

            if (liItem.hasClass("current"))
            {
                option.prop("selected", true);
            }

            option.data(dataKeys.clickTarget, liItem);

            itemParent.append(option);
        }
    }

    function initHeader()
    {
        const header = tabList.find(`.${classes.leftMenuSidebarHeader}`);
        let sliderButton = header.find(`.${classes.leftMenuSidebarSliderButton}`);

        if (header.length === 0)
        {
            return;
        }

        header.find(`.${classes.expandAllTabListItemGroup}`).on("click", expandAllModules);
        header.find(`.${classes.collapseAllTabListItemGroup}`).on("click", collapseAllModules);

        function sliderClick()
        {
            toggleLeftMenuSidebar(sliderButton.attr("aria-expanded") === "false");
        }

        const moduleContent = sliderButton.closest(`.${classes.menuContentArea}`) as JQuery<HTMLElement>;
        const TRANSITION_DURATION = 230;

        sliderButton.one("click", sliderClick);

        function toggleLeftMenuSidebar(willExpand: boolean)
        {
            const expanded = "expandedLeftMenuSidebar";
            const collapsed = "collapsedLeftMenuSidebar";

            let toClass: string, fromClass: string, svg: Key;
            if (willExpand)
            {
                fromClass = collapsed;
                toClass = expanded;
                svg = "ChevronLeft";
            }
            else
            {
                fromClass = expanded;
                toClass = collapsed;
                svg = "ChevronRight";
            }
            moduleContent.switchClass(fromClass, toClass, TRANSITION_DURATION, undefined, function ()
            {
                sliderButton.find("svg").replaceWith(getSvg(svg, "LeftMenuSidebar"));
                if (tabList.length)
                {
                    tabList.toggleClass(classes.tablistCollapsed, !willExpand);
                }
                sliderButton.one("click", sliderClick);
                sliderButton.attr("aria-expanded", willExpand.toString());
                moduleContent.trigger("resize");
                localEventCallbacks.toggleLeftMenuSidebar.dispatchCallbacks();
            });
        }
    }
}